import {
    provideKeycloak,
    createInterceptorCondition,
    IncludeBearerTokenCondition,
    INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
    withAutoRefreshToken,
    AutoRefreshTokenService,
    UserActivityService
  } from 'keycloak-angular';
  import Keycloak, { KeycloakLogoutOptions } from 'keycloak-js';
  import { environment } from '../environments/environment';
  import { APP_INITIALIZER, Provider } from '@angular/core';
  import { TokenService } from './services/token.service';

  Object.defineProperty(globalThis, 'provideAppInitializer', {
    value: function(fn: any) {
      return {
        provide: 'APP_INITIALIZER',
        useFactory: () => fn,
        multi: true
      };
    }
  });

  // Define your API URL pattern that should include the bearer token
  const apiCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
    urlPattern: /^(https:\/\/auth\.wings-ict-solutions\.dev)(\/.*)?$/i
  });

  // Get config from window if available
  const getKeycloakConfig = () => {
    if ((window as any).keycloakLoadedConfig) {
      console.log('Using loaded Keycloak config:', (window as any).keycloakLoadedConfig);
      return (window as any).keycloakLoadedConfig;
    }

    console.log(`Using ${environment.production ? 'PRODUCTION' : 'DEVELOPMENT'} Keycloak config`);
    return environment.keycloakConfig;
  };

  // Create and export the Keycloak instance
  export const keycloakInstance = new Keycloak(getKeycloakConfig());

keycloakInstance.didInitialize = false;

  // Expose it globally for components to use
  (window as any).keycloakInstance = keycloakInstance;

// Default logout options with POST method
export const defaultLogoutOptions: KeycloakLogoutOptions = {
  redirectUri: window.location.origin,
  logoutMethod: 'POST'
};

// Use a single initialization promise that can be reused
let initPromise: Promise<boolean> | null = null;

// Function to initialize Keycloak once and cache the promise
export function ensureKeycloakInitialized(options = {}): Promise<boolean> {
  if (!initPromise) {
    console.log('Creating new Keycloak initialization promise');
    
    // Default initialization options
    const defaultOptions = {
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
      checkLoginIframe: false,
      redirectUri: window.location.origin,
      pkceMethod: 'S256',
      enableLogging: true
    };
    
    // Merge default options with any passed-in options
    const initOptions = { ...defaultOptions, ...options };
    
    console.log('Initializing Keycloak with options:', initOptions);
    
    initPromise = keycloakInstance.init(initOptions as any)
.then(authenticated => {
      console.log('Keycloak globally initialized, authenticated:', authenticated);
      keycloakInstance.didInitialize = true;
        
        // Set up the logout URL with POST method
        keycloakInstance.createLogoutUrl(defaultLogoutOptions);
        
      return authenticated;
    }).catch(error => {
      console.error('Error initializing global Keycloak instance:', error);
      // Clear the promise on failure so we can try again
      initPromise = null;
      return false;
    });
  } else {
    console.log('Reusing existing Keycloak initialization promise');
  }
  
  return initPromise;
}

  // Function to initialize Keycloak and update TokenService
  export function initializeKeycloak(tokenService: TokenService): () => Promise<boolean> {
    return () => {
      return ensureKeycloakInitialized()
        .then(authenticated => {
          if (authenticated && keycloakInstance.token) {
            // Update the token service with the initial token
            tokenService.updateFromKeycloak();
          }
          return authenticated;
        });
    };
  }

  // App initializer provider for Keycloak
  export function keycloakInitializerProvider(): Provider {
    return {
      provide: APP_INITIALIZER,
      useFactory: (tokenService: TokenService) => initializeKeycloak(tokenService),
      deps: [TokenService],
      multi: true
    };
  }

  // Provide Keycloak for Angular DI
  export const provideKeycloakAngular = () => [
    provideKeycloak({
      config: getKeycloakConfig(),
      initOptions: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
        checkLoginIframe: false,
        redirectUri: window.location.origin,
        pkceMethod: 'S256',
        enableLogging: true
      },
      features: [
        withAutoRefreshToken({
          onInactivityTimeout: 'logout',
          sessionTimeout: 6000000
        })
      ],
      providers: [
        AutoRefreshTokenService,
        UserActivityService,
        {
          provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
          useValue: [apiCondition]
        }
      ]
    }),
    TokenService,
    keycloakInitializerProvider()
  ];